import React, {createContext, useContext, useEffect, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {ChangeProductActions, ChangeProductActionTypes} from './ChangeProductActionTypes';
import {ProductLayoutPricing, Project} from '@taopix/taopix-core/dist/types/types';
import {useTaopix} from '../TaopixContext';
import {CoreEvent} from '@taopix/taopix-core';
import {useUserFlowState} from '../UserFlow/UserFlowContext';
import {useUiState} from '../UserInterface/UserInterfaceContext';

export type AlternativeProductData = {
  collectionUniqueRef: string;
  ref: number;
  code: string;
  name: string;
  description: string;
  moreInformationURL: string;
  upgradeLayoutPreviewImage: string;
  thumbnailImage: string;
  previewImage: string;
  spreads: boolean;
  minPages: number;
  maxPages: number;
  pageCount: number;
  renderFormat: number;
  cover1Active: boolean;
  cover2Active: boolean;
  coverRenderFormat: number;
  pricing: ProductLayoutPricing;
  thumbPath: string;
  highDPIThumbnailImage?: string;
  highDPIPreviewImage?: string;
  highDPIUpgradeLayoutPreviewImage?: string;
};

export type RootAction = ActionType<typeof import('./ChangeProductActionTypes')>;

export type ChangeProductState = {
  changeProductDialogOpen: boolean;
  changeProductPreviewDialogOpen: boolean;
  changeProductPreviewImageDialogOpen: boolean;
  alternativeProducts: Array<AlternativeProductData>;
  upgradeProductData: {};
  canUpgradeProduct: boolean;
  canChangeProduct: boolean;
  children?: any;
};

export type ChangeProductContextState = {
  state: ChangeProductState;
  dispatch: React.Dispatch<RootAction>;
};

const initialState: ChangeProductState = {
  changeProductDialogOpen: false,
  changeProductPreviewDialogOpen: false,
  changeProductPreviewImageDialogOpen: false,
  alternativeProducts: [],
  upgradeProductData: {},
  canUpgradeProduct: true,
  canChangeProduct: true,
};

export const ChangeProductContext = createContext<ChangeProductContextState>(
  {} as ChangeProductContextState
);

const changeProductReducer = (
  state: ChangeProductState,
  action: RootAction
): ChangeProductState => {
  switch (action.type) {
    case ChangeProductActionTypes.SET_CHANGE_PRODUCT_DIALOG: {
      return {
        ...state,
        changeProductDialogOpen: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_CHANGE_PRODUCT_PREVIEW_DIALOG: {
      return {
        ...state,
        changeProductPreviewDialogOpen: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_CHANGE_PRODUCT_PREVIEW_IMAGE_DIALOG: {
      return {
        ...state,
        changeProductPreviewImageDialogOpen: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_ALTERNATIVE_PRODUCTS: {
      return {
        ...state,
        alternativeProducts: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_UPGRADE_PRODUCT: {
      return {
        ...state,
        upgradeProductData: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_CAN_UPGRADE_PRODUCT: {
      return {
        ...state,
        canUpgradeProduct: action.payload,
      };
    }
    case ChangeProductActionTypes.SET_CAN_CHANGE_PRODUCT: {
      return {
        ...state,
        canChangeProduct: action.payload,
      };
    }
    case ChangeProductActionTypes.RESET_STATE: {
      return {
        ...action.payload,
      };
    }
    default:
      return state;
  }
};

export const ChangeProductProvider = (props: Pick<ChangeProductState, 'children'>) => {
  const {children} = props;
  const [state, dispatch] = useReducer(changeProductReducer, {
    ...initialState,
  });
  const app = useTaopix();
  const server = app.core.get('server');
  const ui = useUiState();

  const onFetchAltLayouts = () => {
    server
      .get<any>('/api/project/altLayouts', {
        token: 'token',
        json: true,
      })
      .then((response: any) => {
        // if no alt layouts setCanChangeProduct to false to hide the button in header
        if (response.data.layouts.length === 0) {
          dispatch(ChangeProductActions.setCanChangeProduct(false));
          return;
        }
        // otherwise set the data and show button in header
        dispatch(ChangeProductActions.setCanChangeProduct(true));
        dispatch(ChangeProductActions.setAlternativeProducts(response.data.layouts));
      });
  };

  const onFetchLayoutUpgrades = () => {
    server
      .get('/api/project/upgradeLayout', {json: true, token: 'token'})
      .then((response: any) => {
        // if there is no data setCanUpgrade to false
        if (Object.keys(response.data).length === 0) {
          dispatch(ChangeProductActions.setCanUpgradeProduct(false));
          return;
        }
        // otherwise set the data
        dispatch(ChangeProductActions.setCanUpgradeProduct(true));
        dispatch(ChangeProductActions.setUpgradeProductData(response.data));
      })
      .catch(error => {
        console.error(error);
      });
  };

  const onHandleLayoutChange = (data: {project: Project}) => {
    const {layout} = data.project;
    // do change product call here to determine if to show the button
    if (layout.alternativelayoutsdata.split('').length > 0) {
      onFetchAltLayouts();
    } else {
      // if no altLayouts at all hide button
      dispatch(ChangeProductActions.setCanChangeProduct(false));
    }

    // Also do upgradeLayout call
    if (layout.upgradelayoutref > 0) {
      onFetchLayoutUpgrades();
    } else {
      // no upgradeLayout data
      dispatch(ChangeProductActions.setCanUpgradeProduct(false));
    }
  };

  useEffect(() => {
    // when an undo/redo action has reloaded the layout (change/upgrade product), we need to
    // call the layout change handler to ensure we have the correct alt layouts
    const token = app.core.subscribe(CoreEvent.LAYOUT_RELOADED, (msg: string, payload: any) => {
      onHandleLayoutChange({project: {layout: app.core.get('stateManager').getLayout()}} as any);
    });

    return () => {
      app.core.unsubscribe(token);
    };
  }, [app]);

  useEffect(() => {
    app.core.get('loader').on('onLoad', onHandleLayoutChange);

    return () => {
      app.core.get('loader').off('onLoad', onHandleLayoutChange);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <ChangeProductContext.Provider value={contextValue}>{children}</ChangeProductContext.Provider>
  );
};

export const useChangeProductContext = () => {
  const context = useContext(ChangeProductContext);
  if (context === undefined) {
    throw new Error('useChangeProductContext must be used within a ChangeProductContextProvider');
  }
  return context;
};
